import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Context that provides a refreshKey counter and a triggerRefresh function.
 * Views consume refreshKey in their useEffect dependency arrays to re-fetch
 * data when a pull-to-refresh gesture completes.
 */
const RefreshContext = createContext({
  refreshKey: 0,
  triggerRefresh: () => {},
});

/**
 * Provider component that wraps children with RefreshContext.
 * refreshKey starts at 0 and triggerRefresh increments it.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const RefreshProvider = ({ children }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const value = useMemo(() => ({ refreshKey, triggerRefresh }), [refreshKey, triggerRefresh]);

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
};

RefreshProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook for consuming the RefreshContext.
 * @returns {{ refreshKey: number, triggerRefresh: () => void }}
 */
export const useRefreshContext = () => {
  return useContext(RefreshContext);
};

/**
 * Hook that combines RefreshContext with TanStack Query invalidation.
 * Use this in components that need to refresh both legacy (refreshKey) and
 * TanStack Query-based views simultaneously.
 * @returns {{ refresh: () => void }}
 */
export const useRefreshWithQuery = () => {
  const { triggerRefresh } = useRefreshContext();
  const queryClient = useQueryClient();

  const refresh = useCallback(() => {
    triggerRefresh(); // For non-migrated views
    queryClient.invalidateQueries(); // For TanStack Query views
  }, [triggerRefresh, queryClient]);

  return { refresh };
};

export default RefreshContext;
